import { motion } from "framer-motion";
import { Store, Network, Percent, FileCheck2, ArrowUpRight } from "lucide-react";
import Reveal from "@/components/landing/Reveal";
import { renderTitle, toFullUrl } from "@/components/landing/utils";

const ICONS = [Store, Network, Percent, FileCheck2];
const DEFAULT_QUOTE = "No inviertes en una plataforma. Inviertes en una expansión que puedes ver.";

export default function WhyRubiSection({ block, locations }) {
  const cards = block?.cards || [];
  const locs = (locations?.items || []).filter((l) => l.image_url);
  const states = new Set((locations?.items || []).map((l) => l.state).filter(Boolean)).size;
  const photo = toFullUrl(block?.image_url) || toFullUrl(locs[1]?.image_url || locs[0]?.image_url);
  const photoCaption = block?.image_caption || (locs[1] || locs[0])?.name || "Sucursal Rubí Perfumería";
  const quote = block?.quote || DEFAULT_QUOTE;
  const stat1 = block?.stat_1 || `${(locations?.items || []).length || 39} sucursales`;
  const stat2 = block?.stat_2 || `${states || 17} estados`;

  return (
    <section id="por-que-rubi" className="bg-[#FAFAFA] py-20 md:py-32 relative overflow-hidden" data-testid="why-rubi-section">
      <div className="absolute inset-0 grid-lines opacity-30 pointer-events-none" />
      <div className="max-w-[1300px] mx-auto px-5 sm:px-6 lg:px-10 relative">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left · sticky narrative + real photo */}
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <Reveal><span className="eyebrow">{block?.eyebrow || "Por qué Rubí"}</span></Reveal>
            <Reveal delay={120}>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-[56px] leading-[1.02] text-[#0A0A0A]">
                {renderTitle(block?.title || "No estás invirtiendo en una <em>idea</em>.")}
              </h2>
            </Reveal>
            {block?.subtitle && (
              <Reveal delay={220}>
                <p className="mt-5 text-[#3F3F46] text-base md:text-lg leading-relaxed max-w-md">{block.subtitle}</p>
              </Reveal>
            )}
            <Reveal delay={300}>
              <div className="mt-8 relative rounded-3xl overflow-hidden border border-black/10 aspect-[4/3] bg-[#18181B] group">
                {photo && (
                  <img src={photo} alt={photoCaption} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/85 via-[#0A0A0A]/25 to-transparent" />
                <div className="absolute top-4 left-4 flex items-center gap-2 text-[9px] uppercase tracking-[0.2em] font-mono text-[#FFBF57] bg-[#0A0A0A]/70 backdrop-blur px-2.5 py-1 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FFBF57] animate-pulse" /> Operando hoy
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="text-white font-display text-lg md:text-xl leading-snug">{quote}</div>
                  <div className="mt-3 flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-white/70">
                    <span className="text-[#FFBF57]">{stat1}</span><span>·</span><span className="text-[#FFBF57]">{stat2}</span><span className="hidden sm:inline">·</span><span className="hidden sm:inline truncate">{photoCaption}</span>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Right · bento pillars */}
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {cards.map((c, i) => {
              const Icon = ICONS[i % ICONS.length];
              const dark = i === 0;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ y: -4 }}
                  className={`relative rounded-3xl p-6 md:p-7 border overflow-hidden group ${dark ? "bg-[#0A0A0A] border-[#0A0A0A] text-white" : "bg-white border-black/8 hover:border-[#FFBF57]/60"} transition-colors duration-300 ${i === cards.length - 1 && cards.length % 2 === 1 ? "sm:col-span-2" : ""}`}
                  data-testid={`why-card-${i}`}
                >
                  <div className={`absolute -right-10 -top-10 w-40 h-40 rounded-full blur-3xl transition-opacity duration-500 ${dark ? "bg-[#FFBF57]/25" : "bg-[#FFBF57]/0 group-hover:bg-[#FFBF57]/20"}`} />
                  <div className="relative flex items-start justify-between mb-8">
                    <div className={`w-11 h-11 rounded-2xl flex items-center justify-center border ${dark ? "bg-[#FFBF57] border-[#FFBF57]" : "bg-[#FAFAFA] border-black/10 group-hover:bg-[#FFBF57] group-hover:border-[#FFBF57]"} transition-colors duration-300`}>
                      <Icon size={18} strokeWidth={1.7} className="text-[#0A0A0A]" />
                    </div>
                    <span className={`font-mono text-[11px] tracking-[0.2em] ${dark ? "text-[#FFBF57]" : "text-[#A1A1AA]"}`}>0{i + 1}</span>
                  </div>
                  <div className="relative">
                    <div className={`font-display text-xl md:text-2xl mb-2 ${dark ? "text-white" : "text-[#0A0A0A]"}`}>{c.title}</div>
                    <p className={`text-sm leading-relaxed ${dark ? "text-[#A1A1AA]" : "text-[#3F3F46]"}`}>{c.body}</p>
                    {c.metric && (
                      <div className={`mt-5 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono px-2.5 py-1 rounded-full border ${dark ? "border-[#FFBF57]/40 text-[#FFBF57]" : "border-black/10 text-[#0A0A0A] bg-[#FAFAFA]"}`}>
                        <ArrowUpRight size={10} /> {c.metric}
                      </div>
                    )}
                  </div>
                  <div className={`absolute bottom-0 left-0 h-[3px] bg-[#FFBF57] transition-all duration-500 ${dark ? "w-full" : "w-0 group-hover:w-full"}`} />
                </motion.div>
              );
            })}
          </div>
        </div>

        {block?.message && (
          <Reveal delay={200}>
            <p className="mt-12 md:mt-16 text-xs md:text-sm text-[#52525B] max-w-3xl border-l border-[#FFBF57] pl-4 leading-relaxed">{block.message}</p>
          </Reveal>
        )}
      </div>
    </section>
  );
}
